import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import Button from 'src/components/Button';
import SelectField from 'src/components/SelectField';
import TextField from 'src/components/TextField';
import { useAppDispatch } from 'src/store';
import { addToast } from 'src/toasts/ToastsSlice';

import {
  currentSchoolYearLabel,
  numberOrNull,
  PlacementFormData,
  placementSchema,
} from './adminSetup.schema';
import {
  useCreatePlacementMutation,
  useProvidersQuery,
  useSchoolsQuery,
  useStudentsQuery,
} from './adminSetupApi';
import { describeApiError } from './describeApiError';
import EntityPicker from './EntityPicker';

type Props = {
  providerId?: string;
  studentId?: string;
  schoolId?: string;
  onCreated: () => void;
};

const portalMark = (native: boolean | undefined) => (native ? ' - portal' : '');

const PAYMENT_METHODS = ['1099', '1099 Direct Deposit', 'W-2', 'W-2 Direct Deposit', 'Salary'].map(
  (v) => ({ label: v, value: v }),
);
const DOE_TYPES = ['General', 'Enhanced'].map((v) => ({ label: v, value: v }));

const PlacementForm = ({ providerId, studentId, schoolId, onCreated }: Props) => {
  const dispatch = useAppDispatch();
  const [createPlacement, { isLoading }] = useCreatePlacementMutation();
  const [providerSearch, setProviderSearch] = useState('');
  const [studentSearch, setStudentSearch] = useState('');
  const [schoolSearch, setSchoolSearch] = useState('');
  const providers = useProvidersQuery({ search: providerSearch });
  const students = useStudentsQuery({ search: studentSearch });
  const schools = useSchoolsQuery({ search: schoolSearch });
  const { control, handleSubmit, reset, watch } = useForm<PlacementFormData>({
    resolver: zodResolver(placementSchema),
    mode: 'onChange',
    values: {
      providerId: providerId ?? '',
      studentId: studentId ?? '',
      schoolId: schoolId ?? '',
      schoolYearLabel: currentSchoolYearLabel(),
      startDate: '',
      finishDate: '',
      hours: 0,
      hoursPerDay: '',
      payRate: 0,
      group2Rate: '',
      group3Rate: '',
      paymentMethod: '1099',
      doeInvoiceType: 'General',
      doeRate: '',
    },
  });

  const submit = handleSubmit(async (data) => {
    try {
      await createPlacement({
        providerId: data.providerId,
        studentId: data.studentId,
        schoolId: data.schoolId,
        schoolYearLabel: data.schoolYearLabel,
        startDate: data.startDate,
        finishDate: data.finishDate,
        hours: data.hours,
        hoursPerDay: numberOrNull(data.hoursPerDay),
        payRate: data.payRate,
        group2Rate: numberOrNull(data.group2Rate),
        group3Rate: numberOrNull(data.group3Rate),
        paymentMethod: data.paymentMethod,
        doeInvoiceType: data.doeInvoiceType,
        doeRate: numberOrNull(data.doeRate),
      }).unwrap();
      dispatch(addToast({ text: 'Placement created (Confirmed)', type: 'positive' }));
      onCreated();
      reset();
    } catch (error) {
      dispatch(
        addToast({ text: describeApiError(error, 'Could not create placement'), type: 'negative' }),
      );
      console.error('Could not create placement', error);
    }
  });

  const text = (name: keyof PlacementFormData, label: string, type = 'text') => (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <TextField
          label={label}
          type={type}
          step={type === 'number' ? '0.01' : undefined}
          error={error?.message}
          {...field}
          value={field.value === undefined || field.value === null ? '' : String(field.value)}
        />
      )}
    />
  );

  const select = (
    name: keyof PlacementFormData,
    label: string,
    options: { label: string; value: string }[],
  ) => (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <SelectField
          label={label}
          error={error?.message}
          value={String(field.value ?? '')}
          onValueChange={field.onChange}
          options={options}
        />
      )}
    />
  );

  const picker = (
    name: 'providerId' | 'studentId' | 'schoolId',
    label: string,
    options: { label: string; value: string }[] | undefined,
    isLoading: boolean,
    onSearch: (search: string) => void,
  ) => (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <EntityPicker
          label={label}
          value={field.value}
          onChange={field.onChange}
          options={options}
          isLoading={isLoading}
          onSearch={onSearch}
          error={error?.message}
        />
      )}
    />
  );

  const [pickedProvider, pickedStudent, pickedSchool] = watch([
    'providerId',
    'studentId',
    'schoolId',
  ]);
  const ready = Boolean(pickedProvider && pickedStudent && pickedSchool);

  return (
    <form onSubmit={submit} className="flex flex-col gap-4">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        {picker(
          'providerId',
          'Provider',
          providers.data?.map((p) => ({
            label: `${p.firstName} ${p.lastName}${p.email ? ` (${p.email})` : ''}${portalMark(
              p.isPortalNative,
            )}`,
            value: p.id,
          })),
          providers.isFetching,
          setProviderSearch,
        )}
        {picker(
          'studentId',
          'Student',
          students.data?.map((s) => ({
            label: `${s.firstName} ${s.lastName}${s.nycId ? ` (${s.nycId})` : ''}${portalMark(
              s.isPortalNative,
            )}`,
            value: s.id,
          })),
          students.isFetching,
          setStudentSearch,
        )}
        {picker(
          'schoolId',
          'School',
          schools.data?.map((s) => ({ label: s.name, value: s.id })),
          schools.isFetching,
          setSchoolSearch,
        )}
      </div>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
        {text('schoolYearLabel', 'School year (26-27)')}
        {text('startDate', 'Start date', 'date')}
        {text('finishDate', 'End date', 'date')}
        {text('hours', 'Hours / week', 'number')}
        {text('hoursPerDay', 'Hours / day', 'number')}
        {text('payRate', 'Pay rate ($/h)', 'number')}
        {text('group2Rate', 'Group 2 rate', 'number')}
        {text('group3Rate', 'Group 3+ rate', 'number')}
        {select('paymentMethod', 'Payment method', PAYMENT_METHODS)}
        {select('doeInvoiceType', 'DOE invoice type', DOE_TYPES)}
        {text('doeRate', 'DOE rate ($/h)', 'number')}
        <div className="flex items-end">
          <Button type="submit" isLoading={isLoading} disabled={!ready}>
            Create Para placement
          </Button>
        </div>
      </div>
    </form>
  );
};

export default PlacementForm;
